import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type ThemePref = 'light' | 'dark';

interface ThemeContextValue {
  /** What the user chose. */
  pref: ThemePref;
  /** What is actually showing right now (always equal to pref — no OS detection). */
  resolved: 'light' | 'dark';
  setPref: (pref: ThemePref) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);
let transitionTimer: number | undefined;
const STORAGE_KEY = 'bd.theme';

// Kept identical to the inline boot script in index.html. If these disagree, the page
// paints one theme and then switches — the flash the boot script exists to prevent.
// Light is the default; only an explicit stored 'dark' switches. The OS colour scheme
// is deliberately never consulted.
export function readStoredPref(): ThemePref {
  try {
    if (typeof localStorage === 'undefined') return 'light';
    return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

// Light/dark, light by default (§12). The user may explicitly pick dark; that choice is
// stored and wins on the next visit.
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(readStoredPref);
  const resolved: 'light' | 'dark' = pref;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', pref);
    // Keep the browser chrome in step with the page (§12).
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', pref === 'dark' ? '#000000' : '#0E7490');
  }, [pref]);

  const setPref = useCallback((next: ThemePref) => {
    // Cross-fade colours for one beat while the theme flips (design
    // `html.theme-transition`, DC:61, 260 ms at DC:4096-4108) instead of snapping.
    const root = document.documentElement;
    root.classList.add('theme-transition');
    window.clearTimeout(transitionTimer);
    transitionTimer = window.setTimeout(() => root.classList.remove('theme-transition'), 260);
    setPrefState(next);
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable: the choice lasts for this session only */
    }
  }, []);

  const value = useMemo<ThemeContextValue>(() => ({ pref, resolved, setPref }), [pref, resolved, setPref]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
